import { useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import type { EmblaOptionsType } from "embla-carousel";

const defaultOptions: EmblaOptionsType = { loop: false, align: "start" };

const useEmblaOverflow = (options: EmblaOptionsType = defaultOptions) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(options);

  const [hasOverflow, setHasOverflow] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateOverflow = useCallback((api: typeof emblaApi) => {
    if (!api) return;
    setHasOverflow(api.canScrollPrev() || api.canScrollNext());
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    updateOverflow(emblaApi);
    emblaApi.on("reInit", updateOverflow);
    emblaApi.on("select", updateOverflow);
    emblaApi.on("resize", updateOverflow);
    return () => {
      emblaApi.off("reInit", updateOverflow);
      emblaApi.off("select", updateOverflow);
      emblaApi.off("resize", updateOverflow);
    };
  }, [emblaApi, updateOverflow]);

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();

  return { emblaRef, emblaApi, hasOverflow, canScrollPrev, canScrollNext, scrollPrev, scrollNext };
};

export default useEmblaOverflow;
